import { untrack } from "svelte";
import { on } from "svelte/events";
import type { FilterState } from "~/lib/filters/types";
import { filtersEqual, parseListUrlState } from "~/lib/url-state";
import type { ListingState } from "./create-listing-state.svelte";

export interface SyncListingUrlConfig {
  push: (filters: FilterState, sort: string) => void;
  onPopstate?: () => void;
}

export function syncListingUrl(listing: ListingState, config: SyncListingUrlConfig) {
  // Push a new history entry when filters or sort change. Skip when the URL
  // already matches (initial mount and popstate restoration) so that popstate
  // events don't trigger a redundant push.
  // untrack: config.push closes over caller-scope reactive state (e.g.
  // activeSearchQuery in SearchPage); prevent that from leaking into this
  // effect's dep graph.
  $effect(() => {
    const _filters = listing.activeFilters.current;
    const _sort = listing.activeSort;
    const current = parseListUrlState();
    if (filtersEqual(_filters, current.filters) && _sort === current.sort) return;
    listing.clearScrollSession();
    untrack(() => config.push(_filters, _sort));
  });

  // Listen for back/forward navigation and restore filter/sort state.
  $effect(() => {
    function onPopstate() {
      const restored = parseListUrlState();
      listing.pendingFilters = restored.filters;
      listing.setFiltersImmediately(restored.filters);
      listing.activeSort = restored.sort;
      untrack(() => config.onPopstate?.());
    }
    return on(window, "popstate", onPopstate);
  });
}
